import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Paper,
  Group,
  Text,
  Table,
  ActionIcon,
  Modal,
  Button,
  Badge,
  Select,
  Textarea,
  Checkbox,
  FileButton,
  LoadingOverlay,
  Tooltip,
  Divider,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import "@mantine/dates/styles.css";
import {
  IconPlus,
  IconDownload,
  IconTrash,
  IconEdit,
  IconLockOpen,
  IconSignature,
  IconRefresh,
  IconFile,
  IconArrowLeft,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { UseFormReturnType } from "@mantine/form";
import { ObjectData } from "../types/ObjectFormTypes";
import useTransientStore from "../../../data/transcientStore";
import usePermissions from "../../../hooks/usePermissions";
import { useTranslation } from "../../../hooks/useTranslation";
import * as ContractMemoService from "../../../services/ContractMemoService";
import {
  ContractMemoContractOption,
  ContractMemoListItem,
  ContractMemoTypeOption,
  ContractMemoUserOption,
  ContractMemoFile,
} from "../../../services/ContractMemoService";

interface ContractMemoTabProps {
  form: UseFormReturnType<ObjectData>;
  canModify?: boolean;
}

interface PendingFile {
  id: string;
  file: File;
  fileHeader: string;
}

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default function ContractMemoTab({
  form,
  canModify = true,
}: Readonly<ContractMemoTabProps>) {
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addressSystemId = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 350,
    addressSystemId,
    autoLoad: true,
  });

  const { checkAccess } = usePermissions();
  const canView = checkAccess("CITY_OBJECT_CONTRACT_MEMO_SHOW");
  const canAdd =
    canModify && checkAccess("CITY_OBJECT_CONTRACT_MEMO_ADD");

  const objectContractId = parseInt(form.values.job) || 0;

  // ── State ──────────────────────────────────────────────────────────────
  const [contracts, setContracts] = useState<ContractMemoContractOption[]>([]);
  const [selectedContractId, setSelectedContractId] = useState<number>(0);
  const [memos, setMemos] = useState<ContractMemoListItem[]>([]);
  const [memoTypes, setMemoTypes] = useState<ContractMemoTypeOption[]>([]);
  const [foremen, setForemen] = useState<ContractMemoUserOption[]>([]);
  const [contractorForemen, setContractorForemen] = useState<
    ContractMemoUserOption[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add / edit form state
  const [formOpened, setFormOpened] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [memoDate, setMemoDate] = useState<Date | null>(new Date());
  const [description, setDescription] = useState("");
  const [memoTypeId, setMemoTypeId] = useState<string | null>(null);
  const [foremanId, setForemanId] = useState<string | null>(null);
  const [contractorId, setContractorId] = useState<string | null>(null);
  const [startSigning, setStartSigning] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [existingFiles, setExistingFiles] = useState<ContractMemoFile[]>([]);

  // Release modal
  const [releaseOpened, setReleaseOpened] = useState(false);
  const [releaseMemoId, setReleaseMemoId] = useState<number | null>(null);
  const [releaseComment, setReleaseComment] = useState("");
  const [isReleasing, setIsReleasing] = useState(false);

  const t = (key: string, fallback: string) => getTranslation(key) || fallback;

  // ── Data loading ──────────────────────────────────────────────────────
  const loadMemos = useCallback(
    async (contractId: number) => {
      if (!contractId || !currentUserId || !addressSystemId) return;
      setIsLoading(true);
      try {
        const data = await ContractMemoService.getMemos(
          contractId,
          currentUserId,
          addressSystemId,
        );
        setMemos(data);
      } catch (error) {
        console.error("Failed to load memos:", error);
        notifications.show({
          color: "red",
          title: t("TITLE_ERROR", "Error"),
          message: t("MESSAGE_LOAD_MEMOS_FAILED", "Failed to load memos"),
        });
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUserId, addressSystemId],
  );

  const loadContractDependents = useCallback(
    async (contractId: number) => {
      if (!contractId) return;
      try {
        const [types, fm, cfm] = await Promise.all([
          ContractMemoService.getMemoTypes(addressSystemId),
          ContractMemoService.getForemen(contractId),
          ContractMemoService.getContractorForemen(contractId),
        ]);
        setMemoTypes(types);
        setForemen(fm);
        setContractorForemen(cfm);
      } catch (error) {
        console.error("Failed to load contract data:", error);
      }
    },
    [addressSystemId],
  );

  // Load contracts once
  useEffect(() => {
    const loadContracts = async () => {
      if (!currentUserId || !addressSystemId) return;
      try {
        const data = await ContractMemoService.getContracts(
          currentUserId,
          addressSystemId,
        );
        setContracts(data);

        // Prefer the object's contract, else the default, else the first
        let initial = objectContractId;
        if (!data.some((c) => c.id === initial)) {
          initial =
            data.find((c) => c.isDefaultContract)?.id || data[0]?.id || 0;
        }
        setSelectedContractId(initial);
      } catch (error) {
        console.error("Failed to load contracts:", error);
      }
    };
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, addressSystemId]);

  // Reload when the selected contract changes
  useEffect(() => {
    if (selectedContractId) {
      loadMemos(selectedContractId);
      loadContractDependents(selectedContractId);
    } else {
      setMemos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContractId]);

  // ── Add / edit ────────────────────────────────────────────────────────
  const resetForm = () => {
    setEditingId(null);
    setMemoDate(new Date());
    setDescription("");
    setMemoTypeId(null);
    setForemanId(null);
    setContractorId(null);
    setStartSigning(false);
    setPendingFiles([]);
    setExistingFiles([]);
  };

  const openAdd = () => {
    resetForm();
    setFormOpened(true);
  };

  const openEdit = async (memo: ContractMemoListItem) => {
    try {
      const detail = await ContractMemoService.getMemo(
        memo.id,
        selectedContractId,
        currentUserId,
        addressSystemId,
      );
      setEditingId(detail.id);
      setMemoDate(detail.memoDate ? new Date(detail.memoDate) : null);
      setDescription(detail.description || "");
      setMemoTypeId(detail.memoTypeId ? String(detail.memoTypeId) : null);
      setForemanId(detail.userId ? String(detail.userId) : null);
      setContractorId(
        detail.contractorUserId ? String(detail.contractorUserId) : null,
      );
      setStartSigning(false);
      setPendingFiles([]);
      setExistingFiles(detail.files || []);
      setFormOpened(true);
    } catch (error) {
      console.error("Failed to load memo:", error);
      notifications.show({
        color: "red",
        title: t("TITLE_ERROR", "Error"),
        message: t("MESSAGE_LOAD_MEMO_FAILED", "Failed to load memo"),
      });
    }
  };

  const addPendingFiles = (files: File[]) => {
    if (!files || files.length === 0) return;
    setPendingFiles((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `${Date.now()}_${file.name}_${Math.random()}`,
        file,
        fileHeader: "",
      })),
    ]);
  };

  const validateForm = (): string | null => {
    if (!memoDate) return t("MESSAGE_DATE_REQUIRED", "Date is required");
    if (memoDate.getTime() > new Date().setHours(23, 59, 59, 999))
      return t("MESSAGE_DATE_IN_FUTURE", "Date cannot be in the future");
    if (!memoTypeId) return t("MESSAGE_TYPE_REQUIRED", "Type is required");
    if (!foremanId) return t("MESSAGE_FOREMAN_REQUIRED", "Foreman is required");
    if (!contractorId)
      return t(
        "MESSAGE_CONTRACTOR_REQUIRED",
        "Contractor foreman is required",
      );
    const totalFiles = pendingFiles.length + existingFiles.length;
    if (totalFiles === 0)
      return t("MESSAGE_FILE_REQUIRED", "At least one file is required");
    if (startSigning && totalFiles === 0)
      return t("MESSAGE_FILE_REQUIRED", "At least one file is required");
    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      notifications.show({
        color: "yellow",
        title: t("TITLE_VALIDATION", "Validation"),
        message: validationError,
      });
      return;
    }

    setIsSaving(true);
    try {
      const files = await Promise.all(
        pendingFiles.map(async (pf) => ({
          fileName: pf.file.name,
          fileHeader: pf.fileHeader,
          fileData: await ContractMemoService.fileToBase64(pf.file),
          contentType: pf.file.type,
        })),
      );

      const response = await ContractMemoService.saveMemo({
        id: editingId,
        contractId: selectedContractId,
        userId: currentUserId,
        addressSystemId,
        memoDate: memoDate!.toISOString(),
        description,
        memoTypeId: parseInt(memoTypeId!),
        foremanUserId: parseInt(foremanId!),
        contractorUserId: parseInt(contractorId!),
        files,
        startSigning,
      });

      if (response.errors && response.errors.length > 0) {
        notifications.show({
          color: "yellow",
          title: t("TITLE_SAVED_WITH_WARNINGS", "Saved with warnings"),
          message: response.errors.join("; "),
        });
      } else {
        notifications.show({
          color: "green",
          title: t("TITLE_SUCCESS", "Success"),
          message: t("MESSAGE_MEMO_SAVED", "Memo saved successfully"),
        });
      }

      setFormOpened(false);
      resetForm();
      await loadMemos(selectedContractId);
    } catch (error: any) {
      console.error("Failed to save memo:", error);
      notifications.show({
        color: "red",
        title: t("TITLE_ERROR", "Error"),
        message:
          error?.response?.data?.message ||
          t("MESSAGE_SAVE_MEMO_FAILED", "Failed to save memo"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (memo: ContractMemoListItem) => {
    if (
      !window.confirm(
        t("MESSAGE_CONFIRM_DELETE_MEMO", "Delete this memo?"),
      )
    )
      return;
    try {
      await ContractMemoService.deleteMemo(
        memo.id,
        selectedContractId,
        currentUserId,
        addressSystemId,
      );
      notifications.show({
        color: "green",
        title: t("TITLE_SUCCESS", "Success"),
        message: t("MESSAGE_MEMO_DELETED", "Memo deleted"),
      });
      await loadMemos(selectedContractId);
    } catch (error) {
      console.error("Failed to delete memo:", error);
      notifications.show({
        color: "red",
        title: t("TITLE_ERROR", "Error"),
        message: t("MESSAGE_DELETE_MEMO_FAILED", "Failed to delete memo"),
      });
    }
  };

  // ── Existing file actions (edit modal) ───────────────────────────────────
  const handleDownloadExisting = async (file: ContractMemoFile) => {
    if (!editingId) return;
    try {
      const data = await ContractMemoService.downloadFile(
        file.id,
        editingId,
        selectedContractId,
        currentUserId,
        addressSystemId,
      );
      triggerBlobDownload(data.content, data.fileName || file.name);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  const handleDeleteExisting = async (file: ContractMemoFile) => {
    if (!editingId) return;
    if (
      !window.confirm(t("MESSAGE_CONFIRM_DELETE_FILE", "Delete this file?"))
    )
      return;
    try {
      await ContractMemoService.deleteFile(
        file.id,
        editingId,
        selectedContractId,
        currentUserId,
        addressSystemId,
      );
      setExistingFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch (error) {
      console.error("Failed to delete file:", error);
    }
  };

  // ── Signing ──────────────────────────────────────────────────────────────
  const handleStartSigning = async (memo: ContractMemoListItem) => {
    if (
      !window.confirm(
        t(
          "MESSAGE_CONFIRM_START_SIGNING",
          "Start electronic signing for this memo?",
        ),
      )
    )
      return;
    try {
      await ContractMemoService.startSigning(
        memo.id,
        selectedContractId,
        currentUserId,
        addressSystemId,
      );
      notifications.show({
        color: "green",
        title: t("TITLE_SUCCESS", "Success"),
        message: t("MESSAGE_SIGNING_STARTED", "Signing request created"),
      });
      await loadMemos(selectedContractId);
    } catch (error: any) {
      console.error("Failed to start signing:", error);
      notifications.show({
        color: "red",
        title: t("TITLE_ERROR", "Error"),
        message:
          error?.response?.data?.message ||
          t("MESSAGE_SIGNING_FAILED", "Failed to start signing"),
      });
    }
  };

  const openRelease = (memo: ContractMemoListItem) => {
    setReleaseMemoId(memo.id);
    setReleaseComment("");
    setReleaseOpened(true);
  };

  const handleRelease = async () => {
    if (!releaseMemoId || !releaseComment.trim()) return;
    setIsReleasing(true);
    try {
      await ContractMemoService.releaseSigning(
        releaseMemoId,
        selectedContractId,
        currentUserId,
        addressSystemId,
        releaseComment.trim(),
      );
      notifications.show({
        color: "green",
        title: t("TITLE_SUCCESS", "Success"),
        message: t("MESSAGE_SIGNING_RELEASED", "Signing released"),
      });
      setReleaseOpened(false);
      setReleaseMemoId(null);
      setReleaseComment("");
      await loadMemos(selectedContractId);
    } catch (error) {
      console.error("Failed to release signing:", error);
      notifications.show({
        color: "red",
        title: t("TITLE_ERROR", "Error"),
        message: t("MESSAGE_RELEASE_FAILED", "Failed to release signing"),
      });
    } finally {
      setIsReleasing(false);
    }
  };

  const handleDownloadSigned = async (memo: ContractMemoListItem) => {
    if (!memo.signing) return;
    try {
      const data = await ContractMemoService.downloadSignedFile(
        memo.signing.id,
        selectedContractId,
        currentUserId,
        addressSystemId,
      );
      triggerBlobDownload(data.content, data.fileName);
    } catch (error) {
      console.error("Failed to download signed file:", error);
      notifications.show({
        color: "red",
        title: t("TITLE_ERROR", "Error"),
        message: t("MESSAGE_DOWNLOAD_FAILED", "Failed to download file"),
      });
    }
  };

  const renderSigningStatus = (memo: ContractMemoListItem) => {
    const s = memo.signing;
    if (!s) return <Text size="xs" c="dimmed">—</Text>;
    let color = "blue";
    if (s.statusCode === 99) color = "green";
    else if (s.statusCode === 4) color = "orange";
    return (
      <Group gap={4} wrap="nowrap">
        <Badge color={color} variant="light" size="sm">
          {s.status}
        </Badge>
        {s.hasSignedFiles && s.statusCode === 99 && (
          <Tooltip
            label={t("TITLE_DOWNLOAD_SIGNED", "Download signed document")}
          >
            <ActionIcon
              variant="subtle"
              color="blue"
              size="sm"
              onClick={() => handleDownloadSigned(memo)}
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    );
  };

  if (!canView) {
    return (
      <Text c="dimmed" size="sm">
        {t(
          "MESSAGE_NO_ACCESS_CONTRACT_MEMO",
          "You do not have access to contract memos.",
        )}
      </Text>
    );
  }

  const contractOptions = contracts.map((c) => ({
    value: String(c.id),
    label: c.name,
  }));
  const memoTypeOptions = memoTypes.map((mt) => ({
    value: String(mt.id),
    label: mt.description,
  }));
  const foremanOptions = foremen.map((u) => ({
    value: String(u.id),
    label: u.name,
  }));
  const contractorOptions = contractorForemen.map((u) => ({
    value: String(u.id),
    label: u.name,
  }));

  return (
    <Stack gap="md" pos="relative">
      <LoadingOverlay visible={isLoading} />

      <Group justify="space-between" align="flex-end">
        <Title order={5} c="blue">
          {t("TITLE_CONTRACT_MEMO", "Contract Memo")}
        </Title>
        <Group>
          <ActionIcon
            variant="subtle"
            color="blue"
            onClick={() => loadMemos(selectedContractId)}
            title={t("TITLE_REFRESH", "Refresh")}
          >
            <IconRefresh size={18} />
          </ActionIcon>
          {canAdd && (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={openAdd}
              disabled={!selectedContractId}
            >
              {t("LABEL_ADD_MEMO", "Add Memo")}
            </Button>
          )}
        </Group>
      </Group>

      {contracts.length > 1 && (
        <Select
          label={t("LABEL_SELECT_CONTRACT", "Select contract")}
          data={contractOptions}
          value={selectedContractId ? String(selectedContractId) : null}
          onChange={(value) => setSelectedContractId(value ? parseInt(value) : 0)}
          searchable
          maw={420}
        />
      )}

      <Paper withBorder p="md">
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("LABEL_DATE", "Date")}</Table.Th>
              <Table.Th>{t("LABEL_TYPE", "Type")}</Table.Th>
              <Table.Th>{t("LABEL_MODIFIED", "Modified")}</Table.Th>
              <Table.Th>{t("LABEL_SIGNING_STATUS", "Signing")}</Table.Th>
              <Table.Th ta="right">{t("LABEL_ACTIONS", "Actions")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {memos.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" size="sm">
                    {t("MESSAGE_NO_MEMOS", "No memos")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {memos.map((memo) => (
              <Table.Tr key={memo.id}>
                <Table.Td>
                  {new Date(memo.memoDate).toLocaleDateString()}
                </Table.Td>
                <Table.Td>{memo.memoTypeDescription || "—"}</Table.Td>
                <Table.Td>
                  {memo.modified
                    ? `${new Date(memo.modified).toLocaleDateString()}${
                        memo.modifierName ? ` / ${memo.modifierName}` : ""
                      }`
                    : "—"}
                </Table.Td>
                <Table.Td>{renderSigningStatus(memo)}</Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    {canAdd && !memo.signingLocked && (
                      <>
                        <Tooltip label={t("LABEL_EDIT", "Edit")}>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => openEdit(memo)}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t("LABEL_SIGN", "Sign")}>
                          <ActionIcon
                            variant="subtle"
                            color="teal"
                            onClick={() => handleStartSigning(memo)}
                          >
                            <IconSignature size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t("LABEL_DELETE", "Delete")}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => handleDelete(memo)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </>
                    )}
                    {canAdd && memo.signingLocked && (
                      <Tooltip label={t("LABEL_RELEASE_SIGNING", "Release signing")}>
                        <ActionIcon
                          variant="subtle"
                          color="orange"
                          onClick={() => openRelease(memo)}
                        >
                          <IconLockOpen size={16} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Add / edit modal */}
      <Modal
        opened={formOpened}
        onClose={() => setFormOpened(false)}
        title={
          editingId
            ? t("TITLE_EDIT_MEMO", "Edit Memo")
            : t("TITLE_ADD_MEMO", "Add Memo")
        }
        size="lg"
        radius="md"
        closeOnClickOutside={false}
      >
        <Stack gap="md" pos="relative">
          <LoadingOverlay visible={isSaving} />
          <DatePickerInput
            label={t("LABEL_DATE", "Date")}
            required
            value={memoDate}
            onChange={(value) =>
              setMemoDate(value ? new Date(value as unknown as string) : null)
            }
            valueFormat="DD.MM.YYYY"
            maxDate={new Date()}
          />
          <Select
            label={t("LABEL_TYPE", "Type")}
            required
            data={memoTypeOptions}
            value={memoTypeId}
            onChange={setMemoTypeId}
            placeholder={t("LABEL_SELECT", "Select")}
            searchable
          />
          <Textarea
            label={t("LABEL_DESCRIPTION", "Description")}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Select
            label={t("LABEL_FOREMAN", "Foreman")}
            required
            data={foremanOptions}
            value={foremanId}
            onChange={setForemanId}
            placeholder={t("LABEL_SELECT", "Select")}
            searchable
          />
          <Select
            label={t("LABEL_CONTRACTOR_CONTACT", "Contractor contact person")}
            required
            data={contractorOptions}
            value={contractorId}
            onChange={setContractorId}
            placeholder={t("LABEL_SELECT", "Select")}
            searchable
          />

          <Divider
            label={t("LABEL_FILES", "Files")}
            labelPosition="left"
          />

          {existingFiles.length > 0 && (
            <Stack gap={4}>
              {existingFiles.map((file) => (
                <Group key={file.id} justify="space-between" wrap="nowrap">
                  <Group gap={6} wrap="nowrap">
                    <IconFile size={16} />
                    <Text size="sm">{file.name}</Text>
                  </Group>
                  <Group gap={2} wrap="nowrap">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      onClick={() => handleDownloadExisting(file)}
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                    {canAdd && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDeleteExisting(file)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Group>
              ))}
            </Stack>
          )}

          {pendingFiles.map((pf, index) => (
            <Group key={pf.id} justify="space-between" wrap="nowrap">
              <Group gap={6} wrap="nowrap">
                <IconFile size={16} />
                <Text size="sm">{pf.file.name}</Text>
              </Group>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() =>
                  setPendingFiles((prev) =>
                    prev.filter((_, i) => i !== index),
                  )
                }
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}

          <FileButton onChange={addPendingFiles} multiple>
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={<IconPlus size={16} />}
              >
                {t("LABEL_ADD_FILES", "Add files")}
              </Button>
            )}
          </FileButton>

          <Checkbox
            label={t("LABEL_START_SIGNING", "Start electronic signing")}
            checked={startSigning}
            onChange={(e) => setStartSigning(e.currentTarget.checked)}
          />

          <Group justify="flex-end" mt="sm">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setFormOpened(false)}
            >
              {t("LABEL_CANCEL", "Cancel")}
            </Button>
            <Button onClick={handleSave} loading={isSaving}>
              {t("LABEL_SAVE", "Save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Release signing modal */}
      <Modal
        opened={releaseOpened}
        onClose={() => setReleaseOpened(false)}
        title={t("TITLE_RELEASE_SIGNING", "Release Signing Lock")}
        size="md"
        radius="md"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t(
              "MESSAGE_RELEASE_SIGNING_DESCRIPTION",
              "This will cancel the active signing request and unlock the memo for editing.",
            )}
          </Text>
          <Textarea
            label={t("LABEL_COMMENT", "Comment")}
            required
            value={releaseComment}
            onChange={(e) => setReleaseComment(e.currentTarget.value)}
            minRows={3}
            autosize
            error={
              releaseComment.trim() === ""
                ? t("MESSAGE_COMMENT_REQUIRED", "Comment is required")
                : null
            }
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setReleaseOpened(false)}>
              {t("LABEL_CANCEL", "Cancel")}
            </Button>
            <Button
              color="orange"
              leftSection={<IconLockOpen size={16} />}
              onClick={handleRelease}
              loading={isReleasing}
              disabled={!releaseComment.trim()}
            >
              {t("LABEL_RELEASE_SIGNING", "Release signing")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
